import { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import DataTable from '../../components/DataTable';
import LegalAreaActions from './LegalAreaActions';
import Heading from '../../components/Text/Heading';
import Button from '../../components/Form/Button';
import { BriefcaseIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Container } from './styles';
import Input from '../../components/Form/Input';
import { LegalAreaData } from '../../types/LegalArea.types';
import { legalAreaService } from '../../api/services';
import { formatDate } from '../../components/DataTable/utils';

const LegalAreas = () => {
  const [legalAreas, setLegalAreas] = useState<LegalAreaData[]>([]);
  const [search, setSearch] = useState<string>('');
  const [deletedItem, setDeletedItem] = useState<string>('');

  const handleSearch = useCallback((e: { target: { value: SetStateAction<string> } }) => {
    setSearch(e.target.value);
  }, []);

  const LegalAreaName = useCallback((info: CellContext<LegalAreaData, unknown>) => <>{info.getValue()}</>, []);

  const DateFormatter = useCallback(
    (info: CellContext<LegalAreaData, unknown>) => formatDate(info.getValue() as string),
    []
  );

  const actions = useCallback(
    (info: CellContext<LegalAreaData, unknown>) => (
      <LegalAreaActions
        setDeletedItem={setDeletedItem}
        id={info.row.getValue('idAreaJuridica')}
        name={info.row.getValue('nomeAreaJuridica')}
      />
    ),
    []
  );

  const columns = useMemo<ColumnDef<LegalAreaData>[]>(
    () => [
      {
        accessorKey: 'idAreaJuridica',
        header: 'Id',
        enableGlobalFilter: false,
      },
      {
        accessorKey: 'nomeAreaJuridica',
        header: 'Nome da área',
        cell: LegalAreaName,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'dataCriacao',
        header: 'Data da criação',
        cell: DateFormatter,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'dataAlteracao',
        header: 'Última alteração',
        cell: DateFormatter,
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'usuarioCriacao',
        header: 'Criado por',
        enableGlobalFilter: true,
      },
      {
        accessorKey: 'action',
        header: '',
        cell: actions,
        enableSorting: false,
        enableGlobalFilter: false,
      },
    ],
    []
  );

  useEffect(() => {
    async function getLegalAreas() {
      const result = await legalAreaService.list();
      if (result) {
        setLegalAreas(result);
      }
    }

    getLegalAreas();
  }, [deletedItem]);

  const navigate = useNavigate();
  const navigateToCreateLegalArea = useCallback(() => {
    navigate('/legal-areas/create');
  }, []);

  return (
    <Container>
      <div className="header">
        <Heading>Áreas jurídicas</Heading>
        <Button onClick={navigateToCreateLegalArea}>
          <BriefcaseIcon />
          Cadastrar nova área
        </Button>
      </div>

      <div className="search-filter-bar">
        <Input
          name="search"
          icon={<MagnifyingGlassIcon />}
          placeholder="Procurar por nome da área jurídica"
          onChange={handleSearch}
        />
      </div>
      <DataTable<LegalAreaData>
        columnVisibility={{ idAreaJuridica: false }}
        globalFilter={search}
        data={legalAreas}
        columns={columns}
      />
    </Container>
  );
};

export default LegalAreas;
